"use strict";
/**
 * Annotation Transformer
 *
 * Transforms annotations between frontend format (flat array) and backend format
 * (unified form_fields array where signatures are just fields with type: 'signature')
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformAnnotationsForBackend = transformAnnotationsForBackend;
exports.transformAnnotationsFromBackend = transformAnnotationsFromBackend;
exports.isBackendFormat = isBackendFormat;
exports.transformSignatureRequirementsForBackend = transformSignatureRequirementsForBackend;
/**
 * Transform frontend annotations (flat array) to backend format (unified form_fields)
 */
function transformAnnotationsForBackend(frontendAnnotations) {
    var formFields = [];
    frontendAnnotations.forEach(function (annotation) {
        var _a, _b;
        var baseField = {
            id: annotation.id,
            name: annotation.fieldName,
            page: annotation.page,
            x: annotation.position.x,
            y: annotation.position.y,
            width: annotation.position.width,
            height: annotation.position.height,
            type: annotation.type,
            label: annotation.label,
            required: annotation.required,
            assignable_to: annotation.assignableTo,
            properties: annotation.properties,
        };
        // For signature fields, add signee_type at the top level
        if (annotation.type === 'signature') {
            // Check top-level signee_type first (set by PropertyPanel), then properties, then assignableTo
            baseField.signee_type = annotation.signee_type ||
                ((_a = annotation.properties) === null || _a === void 0 ? void 0 : _a.signee_type) ||
                (annotation.assignableTo && annotation.assignableTo[0]) ||
                'student';
            // Debug logging
            console.log('[FRONTEND SEND] Signature field being sent to backend:', {
                id: baseField.id,
                name: baseField.name,
                type: baseField.type,
                signee_type: baseField.signee_type,
                assignable_to: baseField.assignable_to,
                required: baseField.required,
                from_top_level: annotation.signee_type,
                from_properties: (_b = annotation.properties) === null || _b === void 0 ? void 0 : _b.signee_type,
                from_assignableTo: annotation.assignableTo && annotation.assignableTo[0],
            });
        }
        formFields.push(baseField);
    });
    return {
        form_fields: formFields,
    };
}
/**
 * Transform backend annotations (unified form_fields) to frontend format (flat array)
 */
function transformAnnotationsFromBackend(backendAnnotations) {
    var annotations = [];
    // Handle case where backend sends null or empty
    if (!backendAnnotations) {
        return annotations;
    }
    // Transform unified form_fields to frontend annotations
    if (backendAnnotations.form_fields && Array.isArray(backendAnnotations.form_fields)) {
        backendAnnotations.form_fields.forEach(function (field) {
            var annotation = {
                id: field.id || field.field_id,
                type: field.type,
                page: field.page,
                position: {
                    x: field.x,
                    y: field.y,
                    width: field.width,
                    height: field.height
                },
                fieldName: field.name || field.field_name,
                label: field.label,
                required: field.required || false,
                assignableTo: field.assignable_to || [],
                properties: field.properties || {},
            };
            // For signature fields, sync signee_type with assignableTo
            if (field.type === 'signature' && field.signee_type) {
                // Keep signee_type at top level only (not in properties)
                annotation.signee_type = field.signee_type;
                // Ensure assignableTo includes the signee_type if not already present
                if (!annotation.assignableTo.includes(field.signee_type)) {
                    annotation.assignableTo = [field.signee_type];
                }
                // Debug logging
                console.log('[FRONTEND RECEIVE] Signature field received from backend:', {
                    id: field.id,
                    name: field.name,
                    type: field.type,
                    signee_type_from_backend: field.signee_type,
                    assignable_to_from_backend: field.assignable_to,
                    final_signee_type: annotation.signee_type,
                    final_assignableTo: annotation.assignableTo,
                });
            }
            annotations.push(annotation);
        });
    }
    // Legacy support: Transform old signature_boxes format (for backward compatibility)
    if (backendAnnotations.signature_boxes && Array.isArray(backendAnnotations.signature_boxes)) {
        backendAnnotations.signature_boxes.forEach(function (box) {
            annotations.push({
                id: box.id,
                type: 'signature',
                page: box.page,
                position: {
                    x: box.x,
                    y: box.y,
                    width: box.width,
                    height: box.height
                },
                fieldName: "signature_".concat(box.id),
                label: box.label || 'Signature',
                required: box.required || false,
                assignableTo: [box.signee_type],
                properties: {
                    signee_type: box.signee_type,
                    level: box.level,
                    field_id: box.field_id,
                    boxStyle: box.box_style,
                    includeDate: box.include_date,
                    includeName: box.include_name,
                },
            });
        });
    }
    return annotations;
}
/**
 * Check if annotations are already in backend format
 */
function isBackendFormat(annotations) {
    return (annotations &&
        typeof annotations === 'object' &&
        'form_fields' in annotations);
}
/**
 * Transform signature requirements for backend (if needed)
 */
function transformSignatureRequirementsForBackend(requirements) {
    // Currently, signature requirements are already in the correct format
    // But we can add transformation logic here if needed
    return requirements;
}
